import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBarcode,
  faFileArrowUp,
  faFileLines,
  faMagicWandSparkles,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import JobSearchField from './JobSearchField'
import PhotoUploadPreview from './PhotoUploadPreview'
import type { InventoryDocument, JobTreadJobOption } from './types'
import {
  DOCUMENT_ACCEPT,
  INVENTORY_STATUSES,
  compressImageFiles,
  formatFileSize,
  readDocumentFiles,
} from './types'
import { useAutoDismiss } from './useAutoDismiss'

export type InventoryAddDraft = {
  name: string
  barcode: string
  notes: string
  quantity: number
  status: string
  imageUrls: string[]
  documents: InventoryDocument[]
  job: JobTreadJobOption | null
}

type InventoryAddFlowProps = {
  draft: InventoryAddDraft
  onPatch: (
    patch: Partial<InventoryAddDraft> | ((prev: InventoryAddDraft) => Partial<InventoryAddDraft>),
  ) => void
  onOpenScanner: () => void
  saving?: boolean
}

export const InventoryAddFlow: React.FC<InventoryAddFlowProps> = ({
  draft,
  onPatch,
  onOpenScanner,
  saving = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [docBusy, setDocBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [docError, setDocError] = useState<string | null>(null)
  useAutoDismiss(photoError, () => setPhotoError(null))
  useAutoDismiss(docError, () => setDocError(null))

  const handlePhotos = async (files: FileList | null) => {
    if (!files?.length) return
    setPhotoBusy(true)
    setPhotoError(null)
    try {
      const urls = await compressImageFiles(files)
      onPatch((prev) => ({ imageUrls: [...prev.imageUrls, ...urls] }))
    } catch (err: unknown) {
      const msg = err instanceof Error && err.message ? err.message : ''
      setPhotoError(
        /memory|allocate|quota/i.test(msg)
          ? 'Not enough memory to process that photo. Choose a smaller image from the gallery, or take the photo at a lower resolution.'
          : msg || 'Could not process photo. Try a smaller image from the gallery.',
      )
    } finally {
      setPhotoBusy(false)
    }
  }

  const handleDocuments = async (files: FileList | null) => {
    if (!files?.length) return
    setDocBusy(true)
    setDocError(null)
    try {
      const docs = await readDocumentFiles(files)
      onPatch((prev) => ({ documents: [...prev.documents, ...docs] }))
    } catch (err: unknown) {
      setDocError(err instanceof Error ? err.message : 'Could not attach document.')
    } finally {
      setDocBusy(false)
    }
  }

  const removeImage = (idx: number) => {
    onPatch((prev) => ({ imageUrls: prev.imageUrls.filter((_, i) => i !== idx) }))
  }

  const removeDocument = (idx: number) => {
    onPatch((prev) => ({ documents: prev.documents.filter((_, i) => i !== idx) }))
  }

  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-4">
      <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">Photos</h4>
          {photoBusy ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" /> : null}
        </div>
        <PhotoUploadPreview
          images={draft.imageUrls}
          alt="Pallet"
          disabled={saving || photoBusy}
          onUpload={(files) => {
            void handlePhotos(files)
          }}
          onAddDataUrls={(urls) => {
            onPatch((prev) => ({ imageUrls: [...prev.imageUrls, ...urls] }))
          }}
          onRemove={removeImage}
        />
        {photoError ? <p className="text-xs text-error mt-2">{photoError}</p> : null}
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Documents</h4>
          {docBusy ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" /> : null}
        </div>
        <p className="text-xs text-base-content/60">Packing slips, PDFs, Excel, or CSV (max 5 MB each).</p>
        <label className="btn btn-outline w-full">
          <FontAwesomeIcon icon={faFileArrowUp} />
          Attach documents
          <input
            type="file"
            accept={DOCUMENT_ACCEPT}
            multiple
            className="hidden"
            disabled={saving || docBusy}
            onChange={(e) => {
              void handleDocuments(e.target.files)
              e.currentTarget.value = ''
            }}
          />
        </label>
        {draft.documents.length > 0 ? (
          <ul className="space-y-2">
            {draft.documents.map((doc, idx) => (
              <li
                key={`${doc.fileName}-${idx}`}
                className="flex items-center gap-2 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-sm"
              >
                <FontAwesomeIcon icon={faFileLines} className="text-base-content/50 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{doc.fileName}</div>
                  <div className="text-xs text-base-content/55">{formatFileSize(doc.size)}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-square"
                  onClick={() => removeDocument(idx)}
                  aria-label={`Remove ${doc.fileName}`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {docError ? <p className="text-xs text-error">{docError}</p> : null}
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <h4 className="font-semibold text-sm">Barcode</h4>
        <div className="flex gap-2">
          <input
            className="input input-bordered flex-1 font-mono"
            value={draft.barcode}
            onChange={(e) => onPatch({ barcode: e.target.value })}
            placeholder="Scan or type barcode"
            disabled={saving}
          />
          <button type="button" className="btn btn-outline" onClick={onOpenScanner} disabled={saving}>
            <FontAwesomeIcon icon={faBarcode} />
            Scan
          </button>
        </div>
        <button type="button" className="btn btn-ghost btn-sm w-full opacity-60" disabled title="Coming soon">
          <FontAwesomeIcon icon={faMagicWandSparkles} />
          Generate barcode (coming soon)
        </button>
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
        <JobSearchField
          label="Location"
          value={draft.job}
          onChange={(job) => onPatch({ job })}
          placeholder="Search JobTread locations..."
          disabled={saving}
        />
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <h4 className="font-semibold text-sm">Details</h4>
        <div className="flex flex-col gap-1.5 w-full">
          <span className="text-sm font-medium">Name</span>
          <input
            className="input input-bordered w-full"
            value={draft.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            placeholder="Defaults from location + barcode if empty"
            disabled={saving}
          />
        </div>
        <div className="flex flex-col gap-1.5 w-full">
          <span className="text-sm font-medium">Notes</span>
          <textarea
            className="textarea textarea-bordered w-full"
            value={draft.notes}
            onChange={(e) => onPatch({ notes: e.target.value })}
            rows={2}
            disabled={saving}
          />
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm self-start px-0"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? 'Hide advanced' : 'Advanced'}
        </button>
        {showAdvanced ? (
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5 w-full">
              <span className="text-sm font-medium">Quantity</span>
              <input
                type="number"
                min={0}
                className="input input-bordered w-full"
                value={draft.quantity}
                onChange={(e) => onPatch({ quantity: Number(e.target.value) })}
                disabled={saving}
              />
            </div>
            <div className="flex flex-col gap-1.5 w-full">
              <span className="text-sm font-medium">Status</span>
              <select
                className="select select-bordered w-full"
                value={draft.status}
                onChange={(e) => onPatch({ status: e.target.value })}
                disabled={saving}
              >
                {INVENTORY_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}

export default InventoryAddFlow
